import React, { ChangeEvent, FC, Fragment, useEffect, useRef, useState } from 'react';
import { BaseModal, Button, Dropdown } from '@cokitchen/cokitchen-components';
import { ReactComponent as UploadIcon } from 'assets/icons/upload-csv-icon.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash-icon.svg';
import { ReactComponent as FileIcon } from 'assets/icons/file.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as DownloadTemplateIcon } from 'assets/icons/download-template.svg';
import { useNavigate } from 'react-router-dom';
import { KITCHEN_ROUTE_KEYS } from 'utils/constants';
import * as XLSX from 'xlsx';
import MonthSelector, { WeekType } from '../month-selector';
import { EntryType } from 'models/kitchen-roster.model';
import useWeeklyRosterContext from 'contexts/weekly-roster.context';
import useTeamListContext from 'contexts/team-list.context';
import { withDropdownList } from 'components/base/dropdown-list';

interface UploadRosterModalType {
  show: boolean;
  onClose: () => void;
  defaultWeek: WeekType;
}

interface UploadRosterFooterType {
  onConfirm: () => void;
  disabled: boolean;
}

const DropdownList = withDropdownList<{ name: string; value: string }>();

const UploadRosterFooter: FC<UploadRosterFooterType> = ({ onConfirm, disabled }) => (
  <Button
    small
    color='primary'
    variant='block'
    className='mx-auto'
    onClick={onConfirm}
    disabled={disabled}>
    Confirm
  </Button>
);

const UploadRosterModal: FC<UploadRosterModalType> = ({ show, onClose, defaultWeek }) => {
  const { addRosterFormData, setAddRosterFormData, department, setDepartment } =
    useWeeklyRosterContext();
  const { departments } = useTeamListContext();

  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState({
    isError: false,
    msg: ''
  });
  const [rosterDateRange, setRosterDateRange] = useState<WeekType | null>(defaultWeek);

  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) setFile(files[0]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileDrop = (e: any) => {
    e.preventDefault();
    if (!rosterDateRange) return;
    const files = e.dataTransfer.files;
    if (files) setFile(files[0]);
  };

  const readFile = () => {
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const fileDetails = workbook.SheetNames[0];
        const rosterDetails: EntryType[] = XLSX.utils.sheet_to_json(workbook.Sheets[fileDetails]);

        const structured = {
          morning: rosterDetails.map((d) => d.Morning_Shift.trim()),
          afternoon: rosterDetails.map((d) => d.Afternoon_Shift.trim()),
          night: rosterDetails.map((d) => d.Night_Shift.trim())
        };

        if (isValidFileContent(rosterDetails)) setAddRosterFormData([structured]);
      };
    }
  };

  const isValidFileContent = (data: any[]): boolean => {
    if (!Array.isArray(data)) {
      setError({
        isError: true,
        msg: 'Invalid format: file must contain an array of records.'
      });
      return false;
    }

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const hasAllShifts =
        row &&
        typeof row.Morning_Shift === 'string' &&
        typeof row.Afternoon_Shift === 'string' &&
        typeof row.Night_Shift === 'string';

      if (!hasAllShifts) {
        setError({
          isError: true,
          msg: `Invalid format at row ${
            i + 1
          }: Each entry must include Morning_Shift, Afternoon_Shift, and Night_Shift as strings.`
        });
        return false;
      }
    }

    return true;
  };

  const getReviewRoute = () =>
    `${KITCHEN_ROUTE_KEYS.REVIEW_WEEKLY_ROSTER}?start_date=${rosterDateRange?.start.format(
      'YYYY-MM-DD'
    )}&end_date=${rosterDateRange?.end.format('YYYY-MM-DD')}&department=${department.value}`;

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href =
      'https://docs.google.com/spreadsheets/d/1EmnAP4ssTFfEATC4KhyiMtSBHQBca3FBPOUoUBHMTMQ/edit?usp=sharing';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    setError({
      isError: false,
      msg: ''
    });
    readFile();
  }, [file, rosterDateRange]);

  return (
    <BaseModal
      isOpen={show}
      title='Upload XLS'
      onRequestClose={onClose}
      footer={
        <UploadRosterFooter
          onConfirm={() => navigate(getReviewRoute())}
          disabled={Object.values(addRosterFormData).flat().length < 1 || error.isError || !file}
        />
      }>
      <div>
        <div className='mb-5'>
          <label className='font-medium inline-block mb-2'>Week</label>
          <MonthSelector value={rosterDateRange} onChange={setRosterDateRange} isForm />
        </div>
        <div className='mb-10'>
          <label className='font-medium inline-block mb-2'>Department</label>
          <Dropdown
            content={
              <button className='border rounded-md border-gray-2 p-2.5 px-3 w-full text-left text-sm'>
                {!department?.name ? (
                  <span className='text-gray-3 text-sm'>Select department</span>
                ) : (
                  department?.name
                )}
              </button>
            }>
            <DropdownList
              list={departments.map((dept) => ({ name: dept.text, value: dept.value }))}
              onSelect={(dept) => setDepartment(dept)}
            />
          </Dropdown>
        </div>

        <div
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          onClick={() => fileUploadRef?.current?.click()}
          className={`py-12 rounded-lg px-4 border border-[#A3A3A3] border-dashed ${
            file ? '' : 'bg-[#fafafa]'
          }  cursor-pointer`}>
          <div>
            <Fragment>
              {file ? (
                <div className='flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-md relative bottom-5'>
                  <div className='flex items-center'>
                    <FileIcon className='mr-2' />
                    <span className='text-sm text-[#1A1A1A]'>{file.name}</span>
                  </div>
                  <TrashIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setAddRosterFormData([
                        {
                          afternoon: [],
                          morning: [],
                          night: []
                        }
                      ]);
                      if (fileUploadRef.current) fileUploadRef.current.value = '';
                    }}
                  />
                </div>
              ) : (
                <div className='flex justify-center'>
                  <UploadIcon />
                </div>
              )}

              <p className='mt-2 text-center text-xs'>
                <span className='text-[#0EA3FD] font-medium underline cursor-pointer'>
                  {file ? 'Replace' : 'Upload'} XLS file
                </span>
                <span className='ml-1 text-[#858585]'>or drag and drop</span>
              </p>
            </Fragment>
          </div>
          <input
            ref={fileUploadRef}
            type='file'
            hidden
            accept='.xlsx, .xls'
            onChange={handleFileChange}
            disabled={!rosterDateRange}
          />
        </div>
        {error.isError ? (
          <div className='bg-[#FEF7E1] py-[4px] px-[10px] mt-3 rounded flex items-center'>
            <InfoIcon className='mr-2' />
            <p className='font-medium text-sm text-[#BD8F05]'>{error.msg}</p>
          </div>
        ) : null}

        <Button
          onClick={downloadTemplate}
          small
          variant='link'
          color='transparent'
          className='text-sm mt-4'>
          <DownloadTemplateIcon className='mx-1' />
          Download Template
        </Button>
      </div>
    </BaseModal>
  );
};

export default UploadRosterModal;
